import React, { useMemo } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import isArray from 'lodash/isArray';
import values from 'lodash/values';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Legend,
  LabelList
} from 'recharts';

// VARIABLES
import { CHART_COLORS } from 'globals.js';

// LOCAL COMPONENTS
import TooltipContent from './tools/Tooltip';

// STYLES
import styles from './chart.module.scss';

// FUNCTIONS
const getColor = (color, i) => CHART_COLORS[color] || color || values(CHART_COLORS)[i];

const xAxisTickFontSize = 12;

const DEFAULT_HEIGHT_MAX_ITEMS = 9;

// MAIN COMPONENT
export default function BarChartComponent({
  className,
  data = [],
  dataKey = 'data',
  dataFormat,
  axisFormat = dataFormat,
  direction = 'horizontal',
  height: defaultHeight = 280,
  isStacked: isStackedProp = true,
  maxBarSize = 50
}) {
  const isMultiBar = useMemo(() => isArray(data[0]?.[dataKey]), [data, dataKey]);

  const isStacked = !isMultiBar ? false : isStackedProp;

  let height = defaultHeight;

  if (data?.length && data.length > DEFAULT_HEIGHT_MAX_ITEMS) {
    height = defaultHeight + (data.length - DEFAULT_HEIGHT_MAX_ITEMS) * 22;
  }

  // Key name for each bar segment
  const dataKeys = useMemo(
    () =>
      !isMultiBar
        ? [dataKey]
        : // Get all unique keys
        data.reduce((arr, datum) => {
          datum[dataKey].forEach(d => {
            if (!arr.includes(d.name)) arr.push(d.name);
          });
          return arr;
        }, []),
    [data, dataKey, isMultiBar]
  );

  // Each segment in a single bar
  // Multi bar will have more than one
  const barSegments = useMemo(
    () =>
      dataKeys.map((key, i) => {
        const barCells = !isMultiBar
          ? data
          // For multi bar, build array by name
          : data.reduce((arr, datum) => {
            let item = {};
            datum[dataKey].forEach(d => {
              if (d.name === key) item = d;
            });
            arr.push(item);
            return arr;
          }, []);
        const segments = {
          dataKey: key,
          fill: isMultiBar ? getColor(barCells.find(c => c.name === key)?.color, i) : undefined,
          cells: barCells.map((c, j) => {
            const cell = {
              onClick: c.onClick
            };
            if (!isMultiBar) cell.fill = getColor(c.color, j);
            return cell;
          })
        };
        return segments;
      }),
    [dataKeys, data, dataKey, isMultiBar]
  );

  const chartData = useMemo(
    () =>
      !isMultiBar
        ? data
        : data.reduce((arr, datum) => {
          const obj = {};
          obj.name = datum.name;
          datum.data.forEach(d => {
            obj[d.name] = d?.[dataKey];
          });
          arr.push(obj);
          return arr;
        }, []),
    [data, dataKey, isMultiBar]
  );

  const isVertical = direction === 'vertical';

  const xAxisTickLineClamp = useMemo(() => {
    const numItems = data.length;
    if (numItems > 8) return 3;
    if (numItems > 4) return 2;
    return 1;
  }, [data.length]);

  const chartDataWithTotal = useMemo(
    () =>
      chartData.map(entry => {
        const total = dataKeys.reduce((sum, key) => sum + (entry[key] || 0), 0);
        return { ...entry, total: total || undefined }; // Don't render 0s
      }),
    [chartData, dataKeys]
  );

  return (
    <ResponsiveContainer width="100%" height={height} debounce={100} className={clsx(styles.chart, className)}>
      <BarChart
        data={chartDataWithTotal}
        layout={direction}
        margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
        stackOffset={isStacked ? 'sign' : undefined}
        barGap={1}
      >
        <CartesianGrid horizontal={false} vertical />
        {isVertical ? (
          <XAxis type="number" tickFormatter={axisFormat} tickLine={false} allowDecimals={false} />
        ) : (
          <XAxis
            type="category"
            dataKey="name"
            height={xAxisTickLineClamp * xAxisTickFontSize + 8}
            tick={<XAxisTick xAxisTickLineClamp={xAxisTickLineClamp} />}
            tickLine={false}
            interval={0}
          />
        )}
        {isVertical ? (
          <YAxis
            width={140} //
            type="category"
            dataKey="name"
            tick={<YAxisTick />}
            tickLine={false}
            interval={0}
          />
        ) : (
          <YAxis type="number" tickFormatter={axisFormat} tickLine={false} allowDecimals={false} />
        )}
        <ReferenceLine x={isVertical ? 0 : undefined} y={!isVertical ? 0 : undefined} />
        <Tooltip content={<TooltipContent dataFormat={dataFormat} />} />
        {barSegments.map(({ dataKey, fill, cells }, i) => (
          <Bar
            key={i}
            dataKey={dataKey}
            fill={fill}
            stackId={isStacked ? 'a' : undefined}
            radius={!isStacked ? (isVertical ? [0, 3, 3, 0] : [3, 3, 0, 0]) : undefined}
            maxBarSize={maxBarSize}
          >
            {cells.map(({ onClick, ...cell }, j) => (
              <Cell
                key={j} //
                onClick={onClick}
                {...cell}
                style={{ cursor: onClick ? 'pointer' : 'default' }}
              />
            ))}
            {(!isStacked || i === barSegments.length - 1) && (
              <LabelList
                dataKey={isStacked ? 'total' : dataKeys[i]}
                position={isVertical ? 'right' : 'top'}
                fill="#000"
                formatter={v => {
                  const label = axisFormat(v);
                  return ![NaN, 'NaN', '0'].includes(label) ? label : undefined;
                }}
              />
            )}
          </Bar>
        ))}
        {!!isMultiBar && <Legend />}
      </BarChart>
    </ResponsiveContainer>
  );
}

function XAxisTick({ payload, width: totalWidth, height, x, y, xAxisTickLineClamp, visibleTicksCount }) {
  const width = Math.floor(totalWidth / visibleTicksCount - 10);
  return (
    <foreignObject x={x - width / 2} y={y} width={width} height={height}>
      <div xmlns="http://www.w3.org/1999/xhtml">
        <div
          className={clsx('text-center', styles.xAxisTick)}
          style={{ WebkitLineClamp: xAxisTickLineClamp, fontSize: xAxisTickFontSize }}
        >
          <span title={payload.value}>{payload.value}</span>
        </div>
      </div>
    </foreignObject>
  );
}

function YAxisTick({ payload, width, y }) {
  const fontSize = 12;
  const height = fontSize + 4;
  return (
    <foreignObject x={0} y={y - height / 2} width={width} height={height}>
      <div xmlns="http://www.w3.org/1999/xhtml" className="d-flex" style={{ height, fontSize }}>
        <div className="flex-grow-1 text-truncate">
          <span title={payload.value}>{payload.value}</span>
        </div>
      </div>
    </foreignObject>
  );
}
